import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';

export interface DocsLink {
  label: string;
  path: string;
}

export interface DocsSection {
  title: string;
  links: DocsLink[];
}

/**
 * Sidebar structure for the public /docs section. Exported so tests and
 * the docs index page can render the same set of links.
 */
export const docsSections: DocsSection[] = [
  {
    title: 'Getting Started',
    links: [
      { label: 'Setup', path: '/docs/getting-started/setup' },
      { label: 'Your First Observation', path: '/docs/getting-started/first-observation' },
    ],
  },
  {
    title: 'Security & Compliance',
    links: [
      { label: 'POPIA Compliance', path: '/docs/security/popia' },
      { label: 'Data Residency', path: '/docs/security/data-residency' },
      { label: 'Data Export', path: '/docs/security/data-export' },
      { label: 'Backups', path: '/docs/security/backups' },
      { label: 'Breach Response', path: '/docs/security/breach-response' },
    ],
  },
  {
    title: 'Curriculum',
    links: [
      { label: 'CAPS Alignment', path: '/docs/curriculum/caps-alignment' },
      { label: 'AI-Assist (Local LLM)', path: '/docs/curriculum/ai-suggestions' },
    ],
  },
];

interface SidebarNavProps {
  pathname: string;
  onNavigate?: () => void;
}

const SidebarNav: React.FC<SidebarNavProps> = ({ pathname, onNavigate }) => (
  <nav aria-label="Documentation" className="space-y-8">
    <Link
      to="/docs"
      onClick={onNavigate}
      className={`block px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
        pathname === '/docs'
          ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/40 dark:text-primary-200'
          : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800'
      }`}
    >
      Overview
    </Link>

    {docsSections.map(section => (
      <div key={section.title}>
        <h3 className="px-3 mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          {section.title}
        </h3>
        <ul className="space-y-1">
          {section.links.map(link => {
            const active = pathname === link.path;
            return (
              <li key={link.path}>
                <Link
                  to={link.path}
                  onClick={onNavigate}
                  aria-current={active ? 'page' : undefined}
                  className={`block px-3 py-2 rounded-lg text-sm transition-colors ${
                    active
                      ? 'bg-primary-50 text-primary-700 font-medium dark:bg-primary-900/40 dark:text-primary-200'
                      : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800'
                  }`}
                >
                  {link.label}
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    ))}
  </nav>
);

const DocsLayout: React.FC = () => {
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Close mobile drawer when route changes
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  return (
    <div className="bg-white dark:bg-gray-900 pt-20 lg:pt-24 pb-20 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Mobile toggle */}
        <div className="lg:hidden mb-4 flex items-center justify-between">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="inline-flex items-center px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
            aria-label="Open documentation menu"
          >
            <Bars3Icon className="w-5 h-5 mr-2" />
            Docs menu
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[16rem_minmax(0,1fr)] gap-8">
          {/* Desktop sidebar */}
          <aside className="hidden lg:block">
            <div className="sticky top-24">
              <SidebarNav pathname={location.pathname} />
            </div>
          </aside>

          {/* Mobile drawer */}
          {drawerOpen && (
            <div className="lg:hidden fixed inset-0 z-40">
              <div
                className="absolute inset-0 bg-black/40"
                onClick={() => setDrawerOpen(false)}
                aria-hidden="true"
              />
              <div className="absolute left-0 top-0 bottom-0 w-72 max-w-[80%] bg-white dark:bg-gray-900 shadow-xl p-4 overflow-y-auto">
                <div className="flex items-center justify-between mb-6">
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">
                    Documentation
                  </span>
                  <button
                    type="button"
                    onClick={() => setDrawerOpen(false)}
                    className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
                    aria-label="Close documentation menu"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>
                <SidebarNav
                  pathname={location.pathname}
                  onNavigate={() => setDrawerOpen(false)}
                />
              </div>
            </div>
          )}

          {/* Main content */}
          <div className="min-w-0">
            <article className="prose prose-gray dark:prose-invert max-w-none">
              <Outlet />
            </article>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocsLayout;
